import React, { useEffect, useState } from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import styles from "./CardImage.module.scss"

const isFallbackLogoSrc = (url) => /b2t_fallback_logo/i.test(String(url || ""))

/**
 * Fits listing/detail photos whether they are full-bleed images or small logos.
 * Large photos use cover; small / ultra-wide assets use contain so content isn't cropped.
 */
const CardImage = ({
  src,
  alt = "",
  className,
  variant = "card",
  fallback = null,
  forceFit,
  onError,
}) => {
  const fallbackFit = forceFit || (isFallbackLogoSrc(src) ? "logo" : null)
  const [fit, setFit] = useState(fallbackFit || "cover")

  useEffect(() => {
    setFit(fallbackFit || "cover")
  }, [src, fallbackFit])

  if (!src) {
    return fallback ? (
      <div className={classNames(styles.frame, styles[variant], className)}>
        {fallback}
      </div>
    ) : null
  }

  const handleLoad = (event) => {
    if (fallbackFit) return

    const { naturalWidth: width, naturalHeight: height } = event.currentTarget
    if (!width || !height) return

    const aspect = width / height
    const isSmall = width < 480 || height < 320 || width * height < 180000
    // Large short landscape graphics (og/social cards). Avoids normal 16:9 photos
    // that are taller, while still covering quote/interview banners.
    const isUltraWideBanner =
      variant === "card" &&
      aspect >= 1.7 &&
      width >= 1000 &&
      height <= 750
    const isLogoLike = aspect > 2.4 || aspect < 0.5

    if (isUltraWideBanner) {
      setFit("banner")
    } else if (isSmall || isLogoLike) {
      setFit("contain")
    } else {
      setFit("cover")
    }
  }

  return (
    <div
      className={classNames(
        styles.frame,
        styles[variant],
        styles[fit],
        className,
      )}
    >
      <img
        key={src}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={onError}
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    </div>
  )
}

CardImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.oneOf(["card", "detail"]),
  fallback: PropTypes.node,
  forceFit: PropTypes.oneOf(["cover", "contain", "banner", "logo"]),
  onError: PropTypes.func,
}

export default CardImage
